/** Single source of truth for map symbology: which shape each asset class
 *  gets, how big it is, and how open/closed state is drawn. Deliberately free
 *  of Leaflet imports so the legend and the unit tests can consume it without
 *  a map runtime (same pattern as oms/outageMapMarkers.ts). */

export type ShapeKind =
  | 'circle'
  | 'triangle'
  | 'triangleDown'
  | 'square'
  | 'diamond'
  | 'hexagon'

export interface SymbolSpec {
  shape: ShapeKind
  /** Circumradius in pixels — center to vertex, so shapes of equal size
   *  occupy comparable visual area. */
  size: number
  /** Open devices draw hollow. */
  hollow: boolean
}

export const SUBSTATION_SYMBOL: SymbolSpec = { shape: 'hexagon', size: 11, hollow: false }
export const TRANSFORMER_SYMBOL: SymbolSpec = { shape: 'triangle', size: 8, hollow: false }
export const METER_SYMBOL: SymbolSpec = { shape: 'circle', size: 3, hollow: false }

/** Device family by function: does it interrupt fault current (diamond), is it
 *  a switching/connection point (square), or neither (capacitor)? Sectionalizers
 *  are switching — they count fault operations and open during a dead interval
 *  but cannot interrupt fault current. Mirrors DeviceType in
 *  src/topology_platform/models/network.py:36-45; every member is listed
 *  explicitly so a new enum value fails the test rather than silently
 *  defaulting. */
const DEVICE_SHAPE: Record<string, ShapeKind> = {
  fuse: 'diamond',
  recloser: 'diamond',
  switch: 'square',
  switch_46kv: 'square',
  normal_open: 'square',
  sectionalizer: 'square',
  capacitor: 'triangleDown',
}

const DEVICE_SIZE: Record<ShapeKind, number> = {
  circle: 3,
  triangle: 8,
  triangleDown: 8,
  square: 7,
  diamond: 8,
  hexagon: 11,
}

/** `normal_status` is the persisted column and the only open/closed source on
 *  map layers; `is_open` is derived in the trace service and exists solely on
 *  the trace payload. */
export function deviceSymbol(
  deviceType: string | null | undefined,
  normalStatus: string | null | undefined,
): SymbolSpec {
  // Matches MapView's long-standing `|| 'fuse'` fallback for a missing type.
  const dtype = deviceType || 'fuse'
  const shape = DEVICE_SHAPE[dtype] ?? 'diamond'
  // normal_open is a device TYPE that encodes state: a tie point reading as
  // closed is the most consequential symbol error on the map, so it is hollow
  // unconditionally.
  const hollow = dtype === 'normal_open' || normalStatus === 'open'
  return { shape, size: DEVICE_SIZE[shape], hollow }
}

export interface MarkerStyle {
  fill: boolean
  weight: number
  color: string
  fillColor?: string
  fillOpacity?: number
}

/** Leaflet Path option names, NOT SVG attribute names. `fill: 'none'` would be
 *  wrong: Canvas._fillStroke tests `if (options.fill)` and the string 'none' is
 *  truthy, so a canvas marker would still fill. */
export function markerStyle(color: string, hollow: boolean): MarkerStyle {
  return hollow
    ? { fill: false, weight: 2.5, color }
    : { fill: true, weight: 1.5, color: '#fff', fillColor: color, fillOpacity: 0.9 }
}

/** Vertex angles in degrees, screen space (y grows downward). */
const SHAPE_ANGLES: Record<ShapeKind, number[]> = {
  circle: [],
  triangle: [-90, 30, 150],
  triangleDown: [90, 210, 330],
  square: [-45, 45, 135, 225],
  diamond: [-90, 0, 90, 180],
  hexagon: [0, 60, 120, 180, 240, 300],
}

/** Vertex ring for a shape centered at (cx, cy) with circumradius r. Returns
 *  an empty array for 'circle', whose caller uses Leaflet's own arc path. */
export function shapeVertices(
  shape: ShapeKind,
  cx: number,
  cy: number,
  r: number,
): [number, number][] {
  return SHAPE_ANGLES[shape].map(deg => {
    const rad = (deg * Math.PI) / 180
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)]
  })
}
